import { useEffect, useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { getTimezones } from '@/client/sdk.gen';

interface TimezoneSelectProps {
  value?: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

export function TimezoneSelect({
  value,
  onValueChange,
  placeholder = 'Select timezone...',
  disabled = false,
}: TimezoneSelectProps) {
  const [open, setOpen] = useState(false);
  const [timezones, setTimezones] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadTimezones = async () => {
      try {
        const response = await getTimezones();
        if (response.data?.timezones) {
          setTimezones(response.data.timezones);
        }
      } catch (error) {
        console.error('Failed to load timezones:', error);
      } finally {
        setLoading(false);
      }
    };

    loadTimezones();
  }, []);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
          disabled={disabled}
        >
          {value
            ? timezones.find((timezone) => timezone === value)
            : placeholder}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0"
        style={{ width: 'var(--radix-popover-trigger-width)' }}
      >
        <Command className="w-full">
          <CommandInput placeholder="Search timezone..." className="h-9" />
          <CommandEmpty>No timezone found.</CommandEmpty>
          <CommandGroup className="max-h-[300px] overflow-auto">
            {loading ? (
              <CommandItem disabled>Loading timezones...</CommandItem>
            ) : (
              timezones.map((timezone) => (
                <CommandItem
                  key={timezone}
                  value={timezone}
                  onSelect={() => {
                    onValueChange(timezone);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value === timezone ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  {timezone}
                </CommandItem>
              ))
            )}
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
